import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Iconify } from '../../../../components';
import { callApi } from '../../../../utils/apiCall';
import useFeaturePermission from '../../../../hooks/useFeaturePermission';
import RecordingModal from '../../../../pages/dashboard/callLogs/RecordingModal';
import useCallDetail from '../../../../pages/dashboard/callLogs/useCallDetail';
import { OUTCOME } from '../../../../pages/dashboard/callLogs/callLog.constants';
import {
    DESK_CALL_URLS, SUMMARY_SOURCE, deskCallsOf, deskPhoneConnectedOf, summaryStatusText, recordingText, canOfferGenerate, generateOutcome, prefillRows,
} from './leadDeskCalls.api';

const formatDuration = (sec) => {
    if (sec == null) return '';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
};

const OUTCOME_CHIP = {
    [OUTCOME.ANSWERED]: { label: 'Answered', color: 'success' },
    [OUTCOME.MISSED]: { label: 'Missed', color: 'error' },
    [OUTCOME.VOICEMAIL]: { label: 'Voicemail', color: 'warning' },
    [OUTCOME.BUSY]: { label: 'Busy', color: 'warning' },
    [OUTCOME.FAILED]: { label: 'Failed', color: 'default' },
};

// "Details from this call": B4's lead pre-fill summary (name, treatment
// interest, call-back time), each with where it came from. Fetched only when
// opened, so the list costs one request.
function CallDetails({ practiceId, callId }) {
    const [state, setState] = useState({ loading: true, rows: [], error: null });
    useEffect(() => {
        const controller = new AbortController();
        callApi(DESK_CALL_URLS.leadPrefill(practiceId, callId), 'GET', null, false, { signal: controller.signal }).then((response) => {
            if (controller.signal.aborted) return;
            if (response.ok) setState({ loading: false, rows: prefillRows(response.data?.summary), error: null });
            else setState({ loading: false, rows: [], error: response.status === 404 ? null : 'Could not read the details from this call.' });
        });
        return () => controller.abort();
    }, [practiceId, callId]);
    if (state.loading) return <CircularProgress size={16} />;
    if (state.error) return <Typography variant="caption" color="error">{state.error}</Typography>;
    if (!state.rows.length) return <Typography variant="caption" color="text.secondary">Nothing to pick out of this call (no name, treatment or call-back time mentioned).</Typography>;
    return (
        <Stack spacing={0.5} data-testid={`desk-call-details-${callId}`}>
            {state.rows.map((row) => (
                <Stack key={row.key} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>{row.label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{row.value}</Typography>
                    <Chip size="small" variant="outlined" label={row.source} sx={{ height: 20, fontSize: '0.7rem' }} />
                </Stack>
            ))}
        </Stack>
    );
}

CallDetails.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    callId: PropTypes.string.isRequired,
};

function DeskCallRow({ call, practiceId, canManage, ownAiOff, onOpen, onGenerate, generating, note }) {
    const [showDetails, setShowDetails] = useState(false);
    const outcome = OUTCOME_CHIP[call.outcome];
    const summary = call.aiReview?.summary;
    const recText = recordingText(call);
    // Once the practice's own AI is known to be off (409 own_ai_off), the
    // button would only ever repeat that refusal, so it stays hidden.
    const generate = !ownAiOff && canOfferGenerate(call, canManage);
    const details = (
        <>
            <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => setShowDetails((v) => !v)} endIcon={<Iconify icon={showDetails ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} />}>
                Details from this call
            </Button>
            <Collapse in={showDetails} unmountOnExit>
                <Box sx={{ mt: 0.5 }}><CallDetails practiceId={practiceId} callId={call.id} /></Box>
            </Collapse>
        </>
    );
    return (
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }} data-testid={`desk-call-${call.id}`}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Iconify icon={call.direction === 'inbound' ? 'solar:incoming-call-rounded-bold' : 'solar:outgoing-call-rounded-bold'} width={18} sx={{ color: 'text.secondary' }} />
                    <Typography variant="subtitle2">{call.direction === 'inbound' ? 'Incoming' : 'Outgoing'}</Typography>
                    <Typography variant="body2" color="text.secondary">{call.timestampHuman}</Typography>
                    {outcome && <Chip size="small" label={outcome.label} color={outcome.color} variant="outlined" sx={{ height: 22 }} />}
                    {call.durationSec ? <Typography variant="caption" color="text.secondary">{formatDuration(call.durationSec)}</Typography> : null}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    {(call.hasRecording || call.transcriptAvailable) && (
                        <Button size="small" variant="outlined" startIcon={<Iconify icon="solar:play-circle-bold" />} onClick={() => onOpen(call)}>
                            {call.hasRecording ? 'Listen' : 'Transcript'}
                        </Button>
                    )}
                </Stack>
            </Stack>
            {(call.numberLabel || call.userName) && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                    {[call.numberLabel && `Line: ${call.numberLabel}`, call.userName && `Answered by ${call.userName}`].filter(Boolean).join(' · ')}
                </Typography>
            )}
            <Box sx={{ mt: 1 }}>
                {summary ? (
                    <>
                        {call.summarySource && <Chip size="small" color="info" variant="outlined" label={SUMMARY_SOURCE[call.summarySource]} sx={{ height: 20, fontSize: '0.7rem', mb: 0.5 }} />}
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{summary}</Typography>
                        {call.topics.length > 0 && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                {call.topics.map((t) => <Chip key={t} size="small" label={t} sx={{ height: 20, fontSize: '0.7rem' }} />)}
                            </Stack>
                        )}
                        {details}
                    </>
                ) : (
                    <>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">{summaryStatusText(call)}</Typography>
                            {generate && (
                                <LoadingButton size="small" variant="contained" loading={generating} onClick={() => onGenerate(call)}>
                                    Generate summary
                                </LoadingButton>
                            )}
                            {!generate && !canManage && call.summaryStatus === 'not_available' && call.outcome === OUTCOME.ANSWERED && call.hasRecording && (
                                <Typography variant="caption" color="text.secondary">Ask someone with Call Logs manage access to generate one.</Typography>
                            )}
                        </Stack>
                        {call.transcriptAvailable && details}
                    </>
                )}
                {note && <Alert severity={note.tone} sx={{ mt: 1, py: 0 }}>{note.message}</Alert>}
                {recText && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{recText}</Typography>}
            </Box>
        </Box>
    );
}

DeskCallRow.propTypes = {
    call: PropTypes.object.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    canManage: PropTypes.bool,
    ownAiOff: PropTypes.bool,
    onOpen: PropTypes.func.isRequired,
    onGenerate: PropTypes.func.isRequired,
    generating: PropTypes.bool,
    note: PropTypes.shape({ tone: PropTypes.string, message: PropTypes.string }),
};

// Elevate desk-phone calls for one lead, newest first (B7 lead-calls, at most
// 50). Twilio and WhatsApp calls stay in the Call History below. When the
// practice has a desk phone connected (`desk_phone_connected`, CU-869fnwcr5)
// the section always shows, with "No desk-phone calls for this patient yet"
// when the lead has none. Renders nothing when the lead has no desk-phone
// calls and the practice has no desk phone (or the backend predates the flag),
// or the user can't view Call Logs (403) / the backend is older (404).
export default function LeadDeskPhoneCalls({ practiceId, leadId, onLoaded }) {
    const { canManage } = useFeaturePermission('call-logs');
    const [state, setState] = useState({ key: null, calls: [], connected: false, loading: true, error: null, hidden: false });
    const [generating, setGenerating] = useState({});
    const [notes, setNotes] = useState({});
    // Practice-wide: our AI is off (Elevate only / Off / server switch).
    const [ownAiOff, setOwnAiOff] = useState(false);
    const [openRow, setOpenRow] = useState(null);
    const key = `${practiceId}:${leadId}`;
    const keyRef = useRef(key);
    keyRef.current = key;
    const detail = useCallDetail(practiceId, openRow);

    const load = useCallback(async () => {
        if (!practiceId || !leadId) return;
        const forKey = `${practiceId}:${leadId}`;
        setState((s) => ({ ...s, key: forKey, loading: true, error: null, ...(s.key === forKey ? {} : { calls: [], connected: false, hidden: false }) }));
        const response = await callApi(DESK_CALL_URLS.forLead(practiceId, leadId), 'GET', null, false, { bustCache: true });
        if (keyRef.current !== forKey) return;
        if (response.ok) setState({ key: forKey, calls: deskCallsOf(response.data), connected: deskPhoneConnectedOf(response.data), loading: false, error: null, hidden: false });
        else if ([403, 404].includes(response.status)) setState({ key: forKey, calls: [], connected: false, loading: false, error: null, hidden: true });
        else setState((s) => ({ ...s, loading: false, error: 'Desk-phone calls could not be loaded.' }));
    }, [practiceId, leadId]);

    useEffect(() => { setOpenRow(null); setNotes({}); setGenerating({}); setOwnAiOff(false); load(); }, [load]);

    const scoped = state.key === key;
    const calls = scoped ? state.calls : [];
    const connected = scoped && state.connected && !state.hidden;
    // An error panel counts as something shown, so the parent doesn't stack its
    // own empty notice under it.
    const shown = calls.length || (state.error ? 1 : 0);
    useEffect(() => {
        if (onLoaded && scoped && !state.loading) onLoaded(shown, { deskPhoneConnected: connected });
    }, [onLoaded, scoped, state.loading, shown, connected]);

    const generate = async (call) => {
        const forKey = key;
        setGenerating((g) => ({ ...g, [call.id]: true }));
        const response = await callApi(DESK_CALL_URLS.generateSummary(practiceId, call.id), 'POST', {});
        if (keyRef.current !== forKey) return;
        const outcome = generateOutcome(response);
        if (response.status === 409 && response.data?.reason === 'own_ai_off') setOwnAiOff(true);
        setGenerating((g) => ({ ...g, [call.id]: false }));
        setNotes((n) => ({ ...n, [call.id]: outcome }));
        if (outcome.nextStatus) {
            setState((s) => ({ ...s, calls: s.calls.map((c) => (c.id === call.id ? { ...c, summaryStatus: outcome.nextStatus } : c)) }));
        }
        if (outcome.refresh) load();
    };

    if (!scoped || state.hidden) return null;
    if (state.loading && !calls.length) return <Box sx={{ px: 2, py: 2 }}><CircularProgress size={20} /></Box>;
    if (!state.error && !calls.length && !connected) return null;

    return (
        <Box sx={{ mb: 2, bgcolor: 'background.paper', borderRadius: 1.5, border: 1, borderColor: 'divider' }} data-testid="lead-desk-phone-calls">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1.5, pb: 1 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Desk phone calls</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {calls.length ? `${calls.length} ${calls.length === 1 ? 'call' : 'calls'} from the practice phone system` : 'From the practice phone system'}
                    </Typography>
                </Box>
                <Button size="small" onClick={load} disabled={state.loading} startIcon={<Iconify icon="eva:refresh-outline" />}>Refresh</Button>
            </Stack>
            {state.error && <Alert severity="warning" sx={{ mx: 2, mb: 1 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{state.error}</Alert>}
            {!state.error && !calls.length && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }} data-testid="lead-desk-phone-calls-empty">
                    No desk-phone calls for this patient yet
                </Typography>
            )}
            {calls.map((call) => (
                <DeskCallRow
                    key={call.id}
                    call={call}
                    practiceId={practiceId}
                    canManage={canManage}
                    ownAiOff={ownAiOff}
                    onOpen={setOpenRow}
                    onGenerate={generate}
                    generating={Boolean(generating[call.id])}
                    note={notes[call.id]}
                />
            ))}
            {openRow && (
                <RecordingModal
                    key={`${practiceId}-${openRow.id}`}
                    open
                    onClose={() => setOpenRow(null)}
                    callData={detail.call}
                    detailStatus={detail.status}
                    onRetryDetail={detail.retry}
                    practiceId={practiceId}
                    onTranscriptReady={load}
                />
            )}
        </Box>
    );
}

LeadDeskPhoneCalls.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // Called once loaded as onLoaded(shown, { deskPhoneConnected }): shown is
    // how many items are shown (the calls, 1 for the error panel, 0 when hidden
    // or empty); deskPhoneConnected is true when the practice has a desk phone,
    // in which case this section is on screen even with 0 calls.
    onLoaded: PropTypes.func,
};
